import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { UserRegDetails } from 'src/app/models/user';

@Component({
  selector: 'app-pre-approve-dialog',
  templateUrl: './pre-approve-dialog.component.html',
  styleUrls: ['./pre-approve-dialog.component.scss'],
})
export class PreApproveDialogComponent {
  showDatePicker = false;
  minDate = new Date();
  hostFilter = new FormControl();
  filteredHosts: UserRegDetails[] = [];

  // Service categorization
  deliveryServices = [
    'Amazon',
    'Flipkart',
    'Myntra',
    'Swiggy',
    'Zomato',
    'Blinkit',
    'BigBasket',
    'Dunzo',
    'other',
  ];
  transportServices = ['Uber', 'Ola', 'Rapido', 'other'];
  homeServices = [
    'Laundrymen',
    'Plumber',
    'Carpenter',
    'Electrician',
    'Milkman',
    'Help',
    'Cook',
    'Cleaner',
    'Painter',
    'other',
  ];
  preApproveForm = this.formbuilder.group({
    selectControl: new FormControl('', [Validators.required]),
    radioControl: new FormControl('', [Validators.required]),
    instructionControl: new FormControl('', [Validators.required]),
    chooseDate: new FormControl(''),
    userControl: new FormControl(''),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UserRegDetails[],
    private formbuilder: FormBuilder
  ) {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    // Set minimum date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minDate = tomorrow;

    this.filteredHosts = this.data;

    // Subscribe to host filter changes
    this.hostFilter.valueChanges.subscribe((value) => {
      this.filteredHosts = this.filterHosts(value || '');
    });
  }

  // Service type checkers
  isDeliveryService(): boolean {
    const selectedService = this.preApproveForm.controls.selectControl.value;
    return this.deliveryServices.includes(selectedService);
  }

  isServiceProvider(): boolean {
    const selectedService = this.preApproveForm.controls.selectControl.value;
    return (
      this.transportServices.includes(selectedService) ||
      this.homeServices.includes(selectedService)
    );
  }

  isTransportService(): boolean {
    const selectedService = this.preApproveForm.controls.selectControl.value;
    return this.transportServices.includes(selectedService);
  }

  showNeighbourSelection(): boolean {
    const instruction = this.preApproveForm.controls.instructionControl.value;
    return (
      instruction === 'Leave with Neighbour' ||
      instruction === 'Meet with Neighbour'
    );
  }

  // Form validation
  isFormValid(): boolean {
    const form = this.preApproveForm;

    // Basic required fields
    if (
      !form.controls.selectControl.value ||
      !form.controls.radioControl.value ||
      !form.controls.instructionControl.value
    ) {
      return false;
    }

    // Date validation
    if (
      form.controls.radioControl.value === '1' &&
      !form.controls.chooseDate.value
    ) {
      return false;
    }

    // Neighbour validation
    if (this.showNeighbourSelection() && !form.controls.userControl.value) {
      return false;
    }

    return true;
  }

  // Event handlers
  onSelectionChange(): void {
    // Reset instruction when service changes
    this.preApproveForm.get('instructionControl')?.setValue('');
    this.preApproveForm.get('userControl')?.setValue('');
    this.hostFilter.setValue('');
  }

  onChange(val: MatRadioChange): void {
    const chooseDateControl = this.preApproveForm.get('chooseDate');

    if (val.value === '0') {
      // Today selected
      this.minDate = new Date();
      chooseDateControl?.clearValidators();
      chooseDateControl?.setValue(new Date().toISOString());
      this.showDatePicker = false;
    } else {
      // Choose date selected
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.minDate = tomorrow;
      chooseDateControl?.setValidators([Validators.required]);
      chooseDateControl?.setValue(null);
      this.showDatePicker = true;
    }

    chooseDateControl?.updateValueAndValidity();
  }

  onChangeInstruction(val: MatRadioChange): void {
    const userControl = this.preApproveForm.get('userControl');

    if (
      val.value === 'Leave with Neighbour' ||
      val.value === 'Meet with Neighbour'
    ) {
      // Neighbour selection required
      this.hostFilter.setValue('');
      userControl?.setValue('');
      userControl?.setValidators([Validators.required]);
    } else {
      // No neighbour selection needed
      userControl?.clearValidators();
      userControl?.setValue('');
    }

    userControl?.updateValueAndValidity();
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedName = event.option.value;
    const selectedHost = this.data.find(
      (host) => `${host.firstname} ${host.lastname}` === selectedName
    );

    if (selectedHost) {
      this.preApproveForm.get('userControl')?.setValue(selectedHost.userId);
    }
  }

  // Utility methods
  private filterHosts(value: string): UserRegDetails[] {
    if (!value) return this.data;

    const filterValue = value.toLowerCase();
    return this.data.filter((host) =>
      `${host.firstname} ${host.lastname}`.toLowerCase().includes(filterValue)
    );
  }

  // Input validation for numeric inputs (if needed)
  inputValidator(event: { charCode: number }): boolean {
    return (
      (event.charCode !== 8 && event.charCode === 0) ||
      (event.charCode >= 48 && event.charCode <= 57)
    );
  }
}
